import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import {
    Box,
    Stack,
    Typography,
    Card,
    Grid,
    CardContent,
} from "@mui/material";
import { useGridApiRef, getGridStringOperators } from "@mui/x-data-grid";
import Chart from "../../components/Chart"
import NodataImg from "../../assets/images/noData.png";
import { useDispatch, useSelector } from "react-redux";
import { UserRetentionApi } from "../../store/analytics/userRetention";
import { ThemeContext } from '../../context/ThemeContext';
import dayjs from "dayjs";
import CustomGridToolbar from "../../components/CustomGridToolbar";
import apiRoutes from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import DataGridComponent from "../../components/DataGridComponent";
import { AiOutlineBell } from 'react-icons/ai'
import { useNavigate } from "react-router-dom";

const UserRetentionColumns = [
    {
        field: "date",
        headerName: "Date",
        flex: 1,
        filterable: false,

        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{data?.row?.date && dayjs(data?.row?.date).format("YYYY-MM-DD")}</Typography>
        </>)
    },
    {
        field: "retention_rate",
        headerName: "Retention Rate",
        flex: 1,
        filterable: false,

    },
    {
        field: "returning_users",
        headerName: "Returning Count",
        flex: 1,
        filterable: false,

    },
    {
        field: "deleted_users",
        headerName: "Deleted Users",
        flex: 1,
        filterable: false,

    },
    {
        field: "uninstall_count",
        headerName: "Uninstall Count",
        flex: 1,
        filterable: false,

    },
];

const timeSpentColumns = [
    {
        field: "module",
        headerName: "Module",
        // width: 200,
        flex: 1,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

        renderCell: ({ row }) => {
            const formatted =
                row?.module
                    ?.replace(/_/g, " ")
                    ?.toLowerCase()
                    ?.replace(/^\w/, c => c.toUpperCase());

            return <Typography>{formatted}</Typography>;
        },
    },
    {
        field: "time",
        headerName: "Spend Time Avg",
        // width: 200,
        flex: 1,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

    },
    {
        field: "user_count",
        headerName: "User count",
        // width: 200,
        flex: 1,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),

    },

]



export default function UserRetention({ graphData, region, state, loading, type, module, monthDaily, startDate, endDate, isMaskingEnabled, schoolType, platform, userType, language }) {
    const [activevsavgtimespent, setActiveVsAvgTimeSpent] = useState({ l1: true, l2: true });
    const [teacherRegister, setTeacherRegistration] = useState({ l1: true, l2: true, l3: true, l4: true });
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [isDownload, setIsDownload] = useState(false)
    const userRetentionTable = useSelector((state) => state?.userRetentionTable);
    const { result: tableResult = [], loading: tableLoading = false, total = 0 } = userRetentionTable || {}; // <-- ensure defaults so rows is never undefined
    const [queryOptions, setQueryOptions] = useState({});
    const { paginationModel, setPaginationModel } = useContext(ThemeContext) || {}; // <-- guard in case context is undefined

    const onFilterChange = useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });
    }, []);
    const toggleTeacherRegistration = useCallback((key) => {
        setTeacherRegistration(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const toggleActiveVsAvgTimeSpent = useCallback((key) => {
        setActiveVsAvgTimeSpent(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const getRowSpacing = useCallback((params) => {
        return {
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
        };
    }, []);
    const handleSortModelChange = useCallback((sortModel) => {
        setQueryOptions({ sortModel: [...sortModel] });
    }, []);

    const transformGraphData = useMemo(() => {
        if (!Array.isArray(graphData?.result)) return [];

        // Group data by time bucket
        const groupedByTime = {};
        const timeOrderSet = new Set(); // Collect time buckets dynamically from API

        graphData.result.forEach(item => {
            if (item.time && item.module && item.module !== "null" && item.module !== "NewMissing") {
                timeOrderSet.add(item.time); // Dynamically collect time buckets
                if (!groupedByTime[item.time]) {
                    // name is used as dataKey for XAxis in LineChartComponent
                    groupedByTime[item.time] = { name: item.time };
                }
                groupedByTime[item.time][item.module] = item.user_count;
            }
        });

        // Convert Set to Array and sort to ensure ascending order
        const timeOrder = Array.from(timeOrderSet).sort((a, b) => {
            const getMinutes = (timeStr) => {
                const match = timeStr.match(/(\d+)(min|hr)/);
                if (match) {
                    const value = parseInt(match[1]);
                    const unit = match[2];
                    return unit === 'hr' ? value * 60 : value;
                }
                return 0; // Default or handle other cases
            };
            return getMinutes(a) - getMinutes(b);
        });

        const chartData = timeOrder
            .map(time => groupedByTime[time])
            .filter(point => point); // Remove any undefined points if a time bucket has no data

        return chartData;

    }, [graphData]);

    const allModuleNames = useMemo(() => {
        if (!Array.isArray(transformGraphData)) return [];
        const uniqueModuleNames = new Set();
        transformGraphData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'name') {
                    uniqueModuleNames.add(key);
                }
            });
        });
        return Array.from(uniqueModuleNames);
    }, [transformGraphData]);

    const timeSpentData = useMemo(() => {
        if (!Array.isArray(graphData?.result)) return [];
        const uniqueItems = [];
        const keys = new Set();
        graphData.result.forEach(item => {
            if (item.time && item.module && item.module !== "null" && item.module !== "NewMissing") {
                const key = `${item.module}-${item.time}`;
                if (!keys.has(key)) {
                    keys.add(key);
                    uniqueItems.push(item);
                }
            }
        });
        return uniqueItems;
    }, [graphData]);

    useEffect(() => {
        // console.log("isMaskingEnabled",isMaskingEnabled);
        const filterModel = queryOptions?.filterModel;
        const items = filterModel?.items;
        const firstItem = items && items.length > 0 ? items[0] : {};
        const quickFilterValues = filterModel?.quickFilterValues;
        const sortModel = queryOptions?.sortModel;
        let query
        if (sortModel?.length > 0) {
            query = {
                // module_type: module,
                school_type: schoolType,
                page: (paginationModel?.page ?? 0) + 1, // <-- guard paginationModel.page access
                page_size: paginationModel?.pageSize ?? 10, // <-- provide a safe default page size
                sort_order: sortModel[0].sort || "desc",
                field: firstItem.field || '',
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        } else {
            query = {
                // module_type: module,
                school_type: schoolType,
                page: (paginationModel?.page ?? 0) + 1, // <-- guard paginationModel.page access
                page_size: paginationModel?.pageSize ?? 10, // <-- provide a safe default page size
                field: firstItem.field || '',
                state: state,
                city: region,
                sort_order: "desc",
                state: state,
                start_date: startDate,
                end_date: endDate,
                language: language,
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        }
        dispatch(UserRetentionApi(query));
    }, [dispatch, queryOptions, paginationModel, region, state, module, startDate, endDate, isMaskingEnabled, schoolType, language]);

    // Transform the data to pivot format
    const transformedData = useMemo(() => {
        const moduleMap = new Map();

        timeSpentData.forEach(item => {
            if (!moduleMap.has(item.module)) {
                moduleMap.set(item.module, { module: item.module });
            }
            moduleMap.get(item.module)[item.time] = item.user_count;
        });

        return Array.from(moduleMap.values());
    }, [timeSpentData]);

    // Get unique time buckets for columns
    const timeBuckets = useMemo(() => {
        const times = new Set();
        timeSpentData.forEach(item => times.add(item.time));
        return Array.from(times);
    }, [timeSpentData]);

    // Create dynamic columns
    const timeSpentColumns = useMemo(() => {
        const columns = [
            {
                field: 'module',
                headerName: 'Module',
                flex: 1,
                minWidth: 200,
            }
        ];

        // Add columns for each time bucket
        timeBuckets.forEach(time => {
            columns.push({
                field: time,
                headerName: time,
                flex: 1,
                minWidth: 100,
                type: 'number',
                valueFormatter: (params) => params.value || 0,
            });
        });

        return columns;
    }, [timeBuckets]);

    const handleUserRetentionTableRowClick = (params) => {
        const moduleName = params?.row?.module;
        const timeBucket = params?.field === "module" ? undefined : params?.field;
        if (!moduleName || !timeBucket) return;

        let path = `/get-analytics/user-retention/${moduleName}/${timeBucket}`;
        if (startDate && endDate) {
            path += `/${startDate}/${endDate}`;
        }
        navigate(path, {
            state: { moduleName, timeBucket },
        });

    };

    return (
        <>
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center" justifyContent={"space-between"}>
                <Typography variant="h3">User Retention</Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Grid container spacing={1}>
                    {graphData?.total_data && Object.entries(graphData.total_data).map(([data, value], index) => {
                        return (
                            <Grid item xs={6} sm={6} md={6} lg={3} mb={1} key={index}>
                                <Box
                                    className='customCard1'
                                    sx={{
                                        transition: 'none !important',
                                        '&:hover': {
                                            transform: 'none !important',
                                        },
                                    }}
                                >
                                    <Stack direction='row' alignItems='center' spacing={1}>
                                        <Box sx={{ fontSize: 25 }}>
                                            <AiOutlineBell size={25} />
                                        </Box>
                                        <Stack>
                                            <Typography sx={{ textTransform: "capitalize" }}>{data.replaceAll("_", " ")}</Typography>
                                            <Typography variant='h4' fontWeight={600}>{value}</Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Grid>
                        )
                    })}
                </Grid>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Chart type={"line"} height={500} data={transformGraphData} module={'userRetention'} lineState={teacherRegister} />
            </Stack>
            {/* user retention table  */}
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
                <DataGridComponent
                    sortingOrder={[]}
                    disableColumnMenu
                    disableColumnSorting
                    loading={loading}
                    pageSize={paginationModel?.pageSize}
                    onPageSizeChange={(newPageSize) => {
                        setPaginationModel({
                            ...paginationModel,
                            pageSize: newPageSize,
                        })
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        height: 500,
                        "& .MuiDataGrid-cell": {
                            cursor: "pointer",
                            fontSize: "16px",
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                            visibility: "hidden",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            "& .MuiDataGrid-sortIcon": {
                                display: "none",
                            },
                            "& .MuiDataGrid-menuIcon": {
                                display: "none",
                            },
                        },

                    }}
                    getRowId={(row) => `${row.module}-${row.time}`}
                    getRowSpacing={getRowSpacing}
                    onCellClick={handleUserRetentionTableRowClick}
                    rows={transformedData}
                    columns={timeSpentColumns}
                    slots={{
                        noRowsOverlay: () => (
                            <Box className="noData">
                                <img src={NodataImg} alt="no data found" />
                            </Box>
                        ),
                        toolbar: (props) =>
                        (CustomGridToolbar({
                            ...props, moduleType: "userRetention",
                            queryOptions: queryOptions,
                            paginationModel: paginationModel,
                            isDownload: isDownload,
                            setIsDownload: setIsDownload,
                            apiUrl: apiRoutes.userRetentionByCourseCsvExport,
                            hasSearch: false,
                            hasfilter: false,
                            region: region,
                            state: state,
                            startDate: startDate,
                            endDate: endDate,
                            additionalQueryParams: {
                                frequency: monthDaily,
                                start_date: startDate,
                                end_date: endDate,
                            }
                        })),
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                />
            </Stack>
            {/* Time spend table  */}
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
                <DataGridComponent
                    sortingOrder={[]}
                    disableColumnMenu
                    disableColumnSorting
                    loading={tableLoading}
                    rowCount={total}
                    pageSize={paginationModel?.pageSize}
                    onPageSizeChange={(newPageSize) => {
                        setPaginationModel({
                            ...paginationModel,
                            pageSize: newPageSize,
                        })
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        height: 500,
                        "& .MuiDataGrid-cell": {
                            cursor: "pointer",
                            fontSize: "16px",
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                            visibility: "hidden",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            "& .MuiDataGrid-sortIcon": {
                                display: "none",
                            },
                            "& .MuiDataGrid-menuIcon": {
                                display: "none",
                            },
                        },
                    }}
                    rows={tableResult ?? []} // <-- ensure rows is array to prevent DataGrid reading .length of undefined
                    getRowId={(r) => r ? r['date'] : undefined}
                    getRowSpacing={getRowSpacing}
                    columns={UserRetentionColumns}
                    // onSortModelChange={handleSortModelChange}
                    // onFilterModelChange={onFilterChange}
                    slots={{
                        noRowsOverlay: () => (
                            <Box className="noData">
                                <img src={NodataImg} alt="no data found" />
                            </Box>
                        ),
                        toolbar: (props) =>
                        (CustomGridToolbar({
                            ...props, moduleType: "UserRetention",
                            queryOptions: queryOptions,
                            paginationModel: paginationModel,
                            isDownload: isDownload,
                            setIsDownload: setIsDownload,
                            apiUrl: apiRoutes.userRetentionCsvExport,
                            hasSearch: false,
                            hasfilter: false,
                            region: region,
                            state: state,
                            startDate: startDate,
                            endDate: endDate,
                            additionalQueryParams: {
                                frequency: monthDaily,
                                start_date: startDate,
                                end_date: endDate,
                            }
                        })),
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                />
            </Stack>

            <BackdropUI open={isDownload} />
        </>
    )
}
